import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Grid, Typography, Tabs, Tab, CardContent, 
  Avatar, Chip, IconButton, Tooltip, List, ListItem, 
  ListItemIcon, ListItemText, ListItemAvatar, ListItemSecondaryAction, 
  Link, useTheme, alpha, Breadcrumbs
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Business as BusinessIcon,
  Email as EmailIcon, Phone as PhoneIcon, Campaign as CampaignIcon,
  People as PeopleIcon, LocationOn as LocationIcon, Category as CategoryIcon,
  WorkOutline as IndustryIcon, ArrowBack as ArrowBackIcon,
  Download as DownloadIcon, OpenInNew as OpenInNewIcon,
  Add as AddIcon, NavigateNext as NavigateNextIcon,
  AutoAwesome as MagicIcon
} from '@mui/icons-material';

// Import custom components
import { Card, MagicLoader } from '../../design-system';
import { Button } from '../../design-system/components/core';
import { EmptyState } from '../../design-system/components/core';

// Import hooks and services
import useClientStore from '../../stores/clientStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import useUIStore from '../../stores/uiStore';
import DataStateHandler from '../../components/core/data/DataStateHandler';
import ClientExport from './ClientExport';
import SecureAvatar from '../../components/ui/SecureAvatar';
import AssignUsersModal from './AssignUsersModal';
import { useThemeTokens, useGradientText } from '../../theme/themeHooks';
import StatusDisplay from '../../components/ui/StatusDisplay';
import { TabPanel } from '../../components/ui';
import { EntityStatusBadge } from '../../components/core';
import { BrandCard, CampaignCard } from '../../components/core/cards';

// Reusable Components
const TabLoading = ({ message }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <MagicLoader size="medium" message={message} />
  </Box>
);

const AddEntityCard = ({ title, onClick, theme, tokens }) => (
  <Card 
    sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 3,
      background: 'transparent',
      border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
      boxShadow: 'none',
      cursor: 'pointer',
      transition: tokens.transitions.standard,
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
      }
    }}
    onClick={onClick}
  >
    <IconButton
      sx={{ 
        mb: 2,
        p: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
        }
      }}
    >
      <AddIcon fontSize="large" />
    </IconButton>
    <Typography variant="subtitle1" align="center" fontWeight={600}>
      {title}
    </Typography>
  </Card>
);

// Client detail page component
const ClientDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { tokens } = useThemeTokens();
  const { style: titleGradient } = useGradientText();
  
  // Store hooks
  const { 
    currentClient, 
    isLoading, 
    criticalError,
    operationError,
    clearError,
    clearOperationError,
    fetchClient, 
    fetchClientBrands,
    fetchClientCampaigns,
    fetchClientUsers,
    removeUserFromClient, 
  } = useClientStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  const { addNotification } = useUIStore();
  
  // Permission checks
  const canUpdate = hasPermission('clients', 'update');
  const canCreateBrand = hasPermission('brands', 'create');
  const canCreateCampaign = hasPermission('campaigns', 'create');
  const canManageUsers = hasPermission('users', 'create');
  const canViewUsers = hasPermission('users', 'read');
  const canUpdateUsers = hasPermission('users', 'update');
  const canExport = hasPermission('users', 'export');

  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [assignUsersModalOpen, setAssignUsersModalOpen] = useState(false);
  
  // Tab data state
  const [tabsData, setTabsData] = useState({
    brands: { items: [], isLoading: false, loaded: false },
    campaigns: { items: [], isLoading: false, loaded: false },
    users: { items: [], isLoading: false, loaded: false }
  });

  // Fetch client on component mount
  useEffect(() => {
    fetchClient(id);
  }, [fetchClient, id]);
  
  // Handle tab change and lazy loading
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Load data for the selected tab if it hasn't been loaded yet
    if (newValue === 1 && !tabsData.brands.loaded) {
      loadTabData('brands', fetchClientBrands);
    } else if (newValue === 2 && !tabsData.campaigns.loaded) {
      loadTabData('campaigns', fetchClientCampaigns);
    } else if (newValue === 3 && !tabsData.users.loaded) {
      loadTabData('users', fetchClientUsers);
    }
  };
  
  // Generic function to load tab data
  const loadTabData = async (tabKey, fetchFn) => {
    setTabsData(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], isLoading: true }
    }));
    
    try {
      const data = await fetchFn(id);
      setTabsData(prev => ({
        ...prev,
        [tabKey]: { items: data || [], isLoading: false, loaded: true }
      }));
    } catch (error) {
      showError(`Failed to load ${tabKey}: ${error.message}`);
      setTabsData(prev => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], isLoading: false }
      }));
    }
  };
  
  // Reload users after assignment change
  const reloadUsers = async () => {
    await loadTabData('users', fetchClientUsers);
  };
  
  // Handle removing a user
  const handleRemoveUser = async (userId) => {
    try {
      await removeUserFromClient(id, userId);
      await reloadUsers();
    } catch (error) {
      showError("Failed to remove user: " + error.message);
    }
  };
  
  // Helper functions
  const getPrimaryContact = () => {
    if (!currentClient?.contacts?.length) return null;
    return currentClient.contacts.find(contact => contact.isPrimary) || currentClient.contacts[0];
  };
  
  const formatAddress = (address) => {
    if (!address) return 'No address provided';
    
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode) parts.push(address.zipCode);
    if (address.country) parts.push(address.country);
    
    return parts.join(', ') || 'No address provided';
  };
  
  // Show operation error notifications
  useEffect(() => {
    if (operationError) {
      addNotification({
        message: operationError,
        type: 'error'
      });
      clearOperationError();
    }
  }, [operationError, addNotification, clearOperationError]);

  // Loading state - only block if we don't have a client
  if (isLoading && !currentClient) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message="Summoning client details..." />
      </Box>
    );
  }

  // Get primary contact
  const primaryContact = getPrimaryContact();

  // Generic tab empty state config
  const tabEmptyStates = {
    brands: {
      title: "No Brands Yet",
      description: "Add your first brand to this client to start creating content and campaigns.",
      icon: <BusinessIcon sx={{ fontSize: 64 }} />,
      action: canCreateBrand ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/brands/create?clientId=${id}`)}
          magical={true}
        >
          Add First Brand
        </Button>
      ) : null,
      addButton: canCreateBrand ? {
        title: "Add New Brand",
        onClick: () => navigate(`/brands/create?clientId=${id}`)
      } : null
    },
    campaigns: {
      title: "No Campaigns Yet",
      description: "Start creating marketing campaigns for this client.",
      icon: <CampaignIcon sx={{ fontSize: 64 }} />,
      action: canCreateCampaign ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/campaigns/create?clientId=${id}`)}
          magical={true}
        >
          Create Campaign
        </Button>
      ) : null,
      addButton: canCreateCampaign ? {
        title: "Create New Campaign",
        onClick: () => navigate(`/campaigns/create?clientId=${id}`)
      } : null
    },
    users: {
      title: "No Team Members Yet",
      description: "Add team members to collaborate on this client's projects.",
      icon: <PeopleIcon sx={{ fontSize: 64 }} />,
      action: canManageUsers ? (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/users/create?clientId=${id}`)}
            magical={true}
          >
            Create New User
          </Button>
          <Button
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => setAssignUsersModalOpen(true)}
          >
            Assign Existing Users
          </Button>
        </Box>
      ) : null
    }
  };

  // Generic entity render functions
  const renderEntityLists = {
    brands: (item) => <BrandCard
      brand={item}
      linkTo={`/brands/${item._id}`}
      onView={() => navigate(`/brands/${item._id}`)}
      onAnalytics={() => navigate(`/analytics/brands/${item._id}`)}
    />,
    campaigns: (item) => <CampaignCard
      campaign={item}
      linkTo={`/campaigns/${item._id}`}
      onView={() => navigate(`/campaigns/${item._id}`)}
      onAnalytics={() => navigate(`/campaigns/${item._id}/analytics`)}
    />,
    users: (user) => (
      <Card sx={{ height: '100%', position: 'relative' }}>
        <CardContent>
          {canUpdateUsers && (
            <IconButton 
              sx={{ position: 'absolute', top: 8, right: 8 }}
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Remove ${user.firstName} ${user.lastName} from this client?`)) {
                  handleRemoveUser(user._id);
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={user.photo}
              alt={`${user.firstName} ${user.lastName}`}
              sx={{ width: 50, height: 50, mr: 2, borderRadius: tokens.borderRadius.medium }}
            >
              {user.firstName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" component="h3" fontWeight={600}>
                {user.firstName} {user.lastName}
              </Typography>
              <Chip 
                label={user.role}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5, borderRadius: tokens.borderRadius.small }}
              />
            </Box>
          </Box>
          
          <List disablePadding>
            {user.email && (
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EmailIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary={user.email}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            )}
            
            {user.phone && (
              <ListItem disablePadding>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PhoneIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary={user.phone}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            )}
          </List>
          
          {canViewUsers && (
            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => navigate(`/users/${user._id}`)}
            >
              View Profile
            </Button>
          )}
        </CardContent>
      </Card>
    )
  };

  // Generic tab content renderer
  const renderTabContent = (tabKey, extraContent = null) => {
    const { items, isLoading } = tabsData[tabKey];
    const emptyState = tabEmptyStates[tabKey];
    const renderItem = renderEntityLists[tabKey];
    
    if (isLoading) {
      return <TabLoading message={`Loading ${tabKey}...`} />;
    }
    
    if (items.length === 0) {
      return (
        <EmptyState
          title={emptyState.title}
          description={emptyState.description}
          icon={emptyState.icon}
          action={emptyState.action}
        />
      );
    }
    
    return (
      <>
        {extraContent}
        <Grid container spacing={3}>
          {items.map(item => (
            <Grid item xs={12} sm={6} md={4} key={item._id}>
              {renderItem(item)}
            </Grid>
          ))}
          
          {emptyState.addButton && (
            <Grid item xs={12} sm={6} md={4}>
              <AddEntityCard
                title={emptyState.addButton.title}
                onClick={emptyState.addButton.onClick}
                theme={theme}
                tokens={tokens}
              />
            </Grid>
          )}
        </Grid>
      </>
    );
  };
  
  // Team tab extra controls
  const teamTabControls = canManageUsers ? (
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
      <Button
        variant="outlined"
        startIcon={<PeopleIcon />}
        onClick={() => setAssignUsersModalOpen(true)}
      >
        Assign More Users
      </Button>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => navigate(`/users/create?clientId=${id}`)}
        magical={true}
      >
        Create New User
      </Button>
    </Box>
  ) : null;
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Critical Error Message - Above content, not replacing it */}
      {criticalError && (
        <Box sx={{ mb: 3 }}>
          <Card
            elevation={0}
            sx={{ 
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              p: 3,
              border: `1px solid ${theme.palette.error.main}`,
            }}
          >
            <Typography variant="h6" gutterBottom>Error Loading Client</Typography>
            <Typography variant="body1" paragraph>{criticalError}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => fetchClient(id)}
              >
                Retry
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/clients')}
              >
                Back to Clients
              </Button>
            </Box>
          </Card>
        </Box>
      )}

      {/* Only show content if we have a client OR if there's an error but we want to show the layout */}
      {currentClient ? (
        <>
          {/* Breadcrumbs navigation */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{ mb: 3 }}
          >
            <Link 
              underline="hover" 
              color="inherit" 
              component={RouterLink} 
              to="/clients"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Clients
            </Link>
            <Typography color="text.primary">{currentClient.name}</Typography>
          </Breadcrumbs>
      
      {/* Header with client info and actions */}
      <Card 
        magical={true} 
        level={2}
        sx={{ mb: 4, overflow: 'visible' }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="flex-start">
            {/* Logo and basic info */}
            <Grid item xs={12} lg={8}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SecureAvatar
                  src={currentClient.logo?.key || currentClient.logo?.url}
                  alt={currentClient.name}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mr: 3,
                    borderRadius: tokens.borderRadius.card,
                  }}
                >
                  {currentClient.name.charAt(0)}
                </SecureAvatar>
              
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography 
                      variant="h4" 
                      component="h1" 
                      sx={{ fontWeight: 600, ...titleGradient }}
                    >
                      {currentClient.name}
                    </Typography>
                    <StatusDisplay status={currentClient.status} />
                    
                    {/* Magical indicator */}
                    {currentClient.magicalElements?.enchantmentLevel && (
                      <Tooltip title="Enhanced with magical elements">
                        <MagicIcon 
                          sx={{ 
                            ml: 1, 
                            color: theme.palette.secondary.main,
                            animation: tokens.animations.glow,
                          }} 
                        />
                      </Tooltip>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <EntityStatusBadge 
                      status={currentClient.industry}
                      icon={<IndustryIcon fontSize="small" />}
                      size="small"
                      variant="outlined"
                    />
                    <EntityStatusBadge status={currentClient.region} entityType="region" />
                    {currentClient.code && (
                      <Typography variant="body2" color="text.secondary">
                        #{currentClient.code}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            {/* Action buttons */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'row', lg: 'row' },
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                gap: 2,
                flexWrap: 'wrap'
              }}>
                {canUpdate && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/clients/${id}/edit`)}
                    magical={true}
                  >
                    Edit
                  </Button>
                )}
                {canExport && 
                 <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => setExportDialogOpen(true)}
                >
                  Export
                </Button>
                }
               
                
                <Button
                  variant="contained"
                  startIcon={<CampaignIcon />}
                  onClick={() => navigate(`/campaigns/create?clientId=${id}`)}
                  magical={true}
                >
                  New Campaign
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Tabs for client details */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="client details tabs"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              minHeight: 56,
            },
            '& .Mui-selected': {
              color: theme.palette.secondary.main,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.secondary.main,
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label="Overview" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Brands" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Campaigns" icon={<CampaignIcon />} iconPosition="start" />
          <Tab label="Team" icon={<PeopleIcon />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={4}>
         {/* Client Details */}
          <Grid item xs={12} md={6}>
            <Card title="Client Details" magical={false}>
              <CardContent>
                <List disablePadding>
                  <ListItem>
                    <ListItemIcon>
                      <CategoryIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={currentClient.subscriptionTier || 'Standard'}
                      secondary="Subscription Tier"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <IndustryIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={currentClient.industry}
                      secondary="Industry"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <BusinessIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={currentClient.region}
                      secondary="Region"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <PeopleIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={currentClient.accountManager ? `${currentClient.accountManager.firstName} ${currentClient.accountManager.lastName}` : 'Not Assigned'}
                      secondary="Account Manager"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
             {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card title="Contact Information" magical={false}>
              <CardContent>
                {primaryContact ? (
                  <List disablePadding>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          {primaryContact.name?.charAt(0) || 'C'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography fontWeight={500}>
                            {primaryContact.name}
                          </Typography>
                        }
                        secondary={primaryContact.position || 'Primary Contact'}
                      />
                    </ListItem>
                    
                    {primaryContact.email && (
                      <ListItem>
                        <ListItemIcon>
                          <EmailIcon color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={primaryContact.email}
                          secondary="Email"
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Send email">
                            <IconButton 
                              edge="end" 
                              component="a" 
                              href={`mailto:${primaryContact.email}`}
                              size="small"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    )}
                    
                    {primaryContact.phone && (
                      <ListItem>
                        <ListItemIcon>
                          <PhoneIcon color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={primaryContact.phone}
                          secondary="Phone"
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Call">
                            <IconButton 
                              edge="end" 
                              component="a" 
                              href={`tel:${primaryContact.phone}`}
                              size="small"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      No contact information available
                    </Typography>
                    {canUpdate && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/clients/${id}/edit`)}
                      >
                        Add Contact
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
             
          {/* Address Information */}
          <Grid item xs={12} md={6}>
            <Card title="Address Information" magical={false}>
              <CardContent>
                <List disablePadding>
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatAddress(currentClient.address)}
                      secondary="Physical Address"
                      primaryTypographyProps={{ whiteSpace: 'pre-line' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
     
          
          {/* Service Configuration */}
          {/* <Grid item xs={12} md={6}>
            <Card 
              title="Service Configuration" 
              magical={true}
              headerProps={{
                action: canUpdate && (
                  <Tooltip title="Edit configuration">
                    <IconButton 
                      size="small"
                      onClick={() => navigate(`/clients/${id}/edit`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }}
            >
              <CardContent>
                {currentClient.serviceConfig ? (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                      Platforms
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {currentClient.serviceConfig.platforms && Object.entries(currentClient.serviceConfig.platforms)
                        .filter(([_, enabled]) => enabled)
                        .map(([platform]) => (
                          <Chip 
                            key={platform} 
                            label={platform?.charAt(0)?.toUpperCase() + platform?.slice(1)} 
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: tokens.borderRadius.small }}
                          />
                        ))}
                    </Box>
                    
                    <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                      Services
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {currentClient.serviceConfig.services && Object.entries(currentClient.serviceConfig.services)
                        .filter(([_, enabled]) => enabled)
                        .map(([service]) => (
                          <Chip 
                            key={service} 
                            label={service
                              .replace(/([A-Z])/g, ' $1')
                              .replace(/^./, str => str.toUpperCase())} 
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ borderRadius: tokens.borderRadius.small }}
                          />
                        ))}
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Posting Frequency:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {currentClient.serviceConfig.postingFrequency === 'custom' 
                            ? `Every ${currentClient.serviceConfig.customFrequency} days` 
                            : currentClient.serviceConfig.postingFrequency?.replace('_', ' ')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Reporting Frequency:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {currentClient.serviceConfig.reportingFrequency?.replace('_', ' ')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography color="textSecondary" gutterBottom>
                      No service configuration available
                    </Typography>
                    {canUpdate && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/clients/${id}/edit`)}
                      >
                        Add Configuration
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid> */}
          
          {/* Notes & Additional Information */}
          {currentClient.notes && (
            <Grid item xs={12}>
              <Card title="Notes" magical={false}>
                <CardContent>
                  <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {currentClient.notes || 'No notes available'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>
      
      {/* Brands Tab */}
      <TabPanel value={tabValue} index={1}>
        {renderTabContent('brands')}
      </TabPanel>
      
      {/* Campaigns Tab */}
      <TabPanel value={tabValue} index={2}>
        {renderTabContent('campaigns')}
      </TabPanel>
      
      {/* Team Tab */}
      <TabPanel value={tabValue} index={3}>
        {renderTabContent('users', teamTabControls)}
      </TabPanel>

          {/* Modals */}
          <ClientExport
            open={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            client={currentClient}
          />
          
          <AssignUsersModal
            open={assignUsersModalOpen}
            onClose={(updated) => {
              setAssignUsersModalOpen(false);
              if (updated) {
                reloadUsers();
              }
            }}
            clientId={id}
          />
        </>
      ) : (
        /* Show DataStateHandler when no client is loaded */
        <DataStateHandler 
          isLoading={isLoading} 
          error={criticalError && !currentClient}
          onRetry={() => fetchClient(id)}
          emptyMessage="Client not found"
          errorMessage={criticalError}
        />
      )}
    </Box>
  );
};

export default ClientDetail;